import type { ApiEnvelope } from "./types";
import { flushClientErrors, reportApiFailure } from "../telemetry/clientErrors";

let csrfToken = "";

/** getRandomValues also works on HTTP LAN deployments, unlike randomUUID. */
export function newClientMutationId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
}

function isMutating(method: string) {
  return !["GET", "HEAD"].includes(method.toUpperCase());
}

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
  const raw = await response.text();
  if (!raw) return { ok: response.ok };
  try {
    return JSON.parse(raw) as ApiEnvelope<T>;
  } catch {
    return { ok: false, error: "接口响应不是 JSON" };
  }
}

async function refreshCsrfToken() {
  const response = await fetch("/api/auth/me", { credentials: "same-origin" });
  const payload = await parseEnvelope(response);
  if (payload.csrfToken) csrfToken = payload.csrfToken;
}

async function ensureCsrfToken(method: string) {
  if (!isMutating(method) || csrfToken) return;
  await refreshCsrfToken().catch(() => {});
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const method = init.method || "GET";
  await ensureCsrfToken(method);
  return requestWithCsrfRetry(path, init, method, false);
}

/** Multipart upload helper — do not set Content-Type so the browser adds the boundary. */
export async function apiForm<T>(path: string, form: FormData, method = "POST"): Promise<ApiEnvelope<T>> {
  await ensureCsrfToken(method);
  return requestWithCsrfRetry(path, { method, body: form, headers: new Headers() }, method, false);
}

async function requestWithCsrfRetry<T>(
  path: string,
  init: RequestInit,
  method: string,
  didRetry: boolean
): Promise<ApiEnvelope<T>> {
  const headers = new Headers(init.headers);
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (!isFormData && !headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
  if (isFormData && headers.has("Content-Type")) headers.delete("Content-Type");
  if (isMutating(method) && csrfToken) headers.set("X-CSRF-Token", csrfToken);

  const response = await fetch(path, {
    ...init,
    method,
    headers,
    credentials: "same-origin"
  });

  const payload = await parseEnvelope<T>(response);

  if (payload.csrfToken) csrfToken = payload.csrfToken;
  if (response.status === 403 && isMutating(method) && !didRetry && String(payload.error || "").includes("CSRF")) {
    csrfToken = "";
    await refreshCsrfToken();
    return requestWithCsrfRetry(path, init, method, true);
  }
  if (!response.ok || !payload.ok) {
    reportApiFailure(path, response.status, payload.error || "请求失败");
    throw new ApiError(payload.error || `请求失败 ${response.status}`, response.status);
  }
  void flushClientErrors();
  return payload;
}

export function clearCsrfToken() {
  csrfToken = "";
}

/** Download a binary API response (CSV/zip/db) with session cookies. */
export async function downloadFile(path: string, fallbackName: string) {
  const response = await fetch(path, { credentials: "same-origin" });
  if (!response.ok) {
    let message = `下载失败 ${response.status}`;
    try {
      const payload = (await response.json()) as { error?: string };
      if (payload.error) message = payload.error;
    } catch {
      // ignore non-JSON error bodies
    }
    throw new ApiError(message, response.status);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const matched = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
  const filename = matched ? decodeURIComponent(matched[1].replace(/"/g, "")) : fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
